import React, { type ReactNode } from 'react';
import cn from 'classnames';
import {
  Checkbox,
  type CheckboxProps,
  CheckboxProvider,
  type CheckboxProviderProps,
} from '@ariakit/react';
import { FormField, type FormFieldProps } from '../FormField';
import { ControlLabel } from '../ControlLabel';
import * as styles from './styles.module.css';
import { useSizeContext } from '../Hooks/useSizeContext';

type ControlProps = Omit<CheckboxProps, 'size'> & {
  size?: 'medium' | 'large';
  className?: string;
};

const sizes = {
  large: 20,
  medium: 16,
};

export function Control({ size = 'large', className = '', ...props }: ControlProps) {
  const sizeContext = useSizeContext() as 'medium' | 'large';
  const mainSize = sizeContext || size;

  return (
    <div className={styles.checkboxControlWrapper}>
      <Checkbox className={cn([styles.checkboxControl, styles[mainSize], className])} {...props} />
      <div className={styles.checkboxControlIcon}>
        <svg
          width={sizes[mainSize]}
          height={sizes[mainSize]}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.62012 10.8699L8.25 13.5L14.37 7.37988"
            stroke="#B845FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

export function Provider(props: CheckboxProviderProps) {
  return <CheckboxProvider {...props} />;
}

export type GroupContainerReturnValue = string | number | boolean | readonly (string | number)[];

type GroupContainerProps = {
  formFieldProps?: FormFieldProps;
  size?: 'large' | 'medium';
  name: string;
  options: {
    [key: string]: string;
  };
  defaultValue: string[];
  onChange: (values: GroupContainerReturnValue) => void;
};

export const GroupContainer = React.forwardRef<HTMLDivElement, GroupContainerProps>(
  ({ formFieldProps, size = 'large', name, options, defaultValue, onChange }, ref) => {
    const keys = Object.keys(options);

    return (
      <Provider
        defaultValue={defaultValue}
        setValue={(values) => {
          onChange(values);
        }}
      >
        <FormField ref={ref} {...formFieldProps} tagElement="div">
          <div>
            {keys.map((key) => (
              <ControlLabel label={options[key]} key={key} size={size}>
                <Control name={name} value={key} />
              </ControlLabel>
            ))}
          </div>
        </FormField>
      </Provider>
    );
  }
);

type SingleCheckboxProps = {
  label: string | ReactNode;
  description?: string;
  size?: 'large' | 'medium';
  checked: boolean;
  value?: string | number;
  onChange: (checked: boolean) => void;
};

export const SingleCheckbox = React.forwardRef<HTMLInputElement, SingleCheckboxProps>(
  ({ label, description, size = 'large', checked, value, onChange }, ref) => {
    const handleChange = (e: React.BaseSyntheticEvent) => {
      const { target } = e;
      onChange(target.checked);
    };

    return (
      <ControlLabel label={label} size={size} description={description} alignment="top">
        <Control checked={checked} onChange={handleChange} value={value} ref={ref} />
      </ControlLabel>
    );
  }
);
